import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AppButtonComponent } from '@hunting-nature/shared-ui/components/app-button/app-button.component';
import { BottomSheetComponent } from '@hunting-nature/shared-ui/components/bottom-sheet/bottom-sheet.component';
import type { BottomSheetSettings } from '@hunting-nature/shared-ui/components/bottom-sheet/bottom-sheet.component';
import { pickIcons } from '@hunting-nature/shared-ui/helper/icon-registry.const';
import { IonCheckbox, IonIcon, IonSkeletonText } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { addIcons } from 'ionicons';
import { Store } from '@ngrx/store';

import { GeneralActions } from '../../../shared/redux/general/general.actions';
import { ConsentActions } from './redux/consent.actions';
import { ConsentSelectors, hasUncheckedOptionalConsents, isMandatoryConsentComplete } from './redux/consent.reducer';
import { GeneralSelectors } from '../../../shared/redux/general/general.reducer';

@Component({
  selector: 'app-consent-modal',
  imports: [BottomSheetComponent, AppButtonComponent, IonCheckbox, IonIcon, IonSkeletonText, TranslocoPipe],
  templateUrl: './consent-modal.component.html',
  styleUrl: './consent-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentModalComponent {
  // services
  private readonly store = inject(Store);

  // UI bindings
  protected readonly isConsentModalOpen$$ = this.store.selectSignal(ConsentSelectors.isConsentModalOpen);
  protected readonly consentModalMode$$ = this.store.selectSignal(ConsentSelectors.consentModalMode);
  protected readonly userConsents$$ = this.store.selectSignal(ConsentSelectors.userConsents);
  protected readonly isUserConsentsLoading$$ = this.store.selectSignal(ConsentSelectors.isUserConsentsLoading);
  protected readonly isMandatoryConsentComplete$$ = this.store.selectSignal(isMandatoryConsentComplete);
  protected readonly hasUncheckedOptionalConsents$$ = this.store.selectSignal(hasUncheckedOptionalConsents);
  protected readonly isConsentUpdateSubmitting$$ = this.store.selectSignal(ConsentSelectors.isConsentUpdateSubmitting);
  private readonly activeAppLanguage$$ = this.store.selectSignal(GeneralSelectors.activeAppLanguage);

  constructor() {
    addIcons({ ...pickIcons('alert-information-icon') });
  }

  readonly bottomSheetSettings: BottomSheetSettings = {
    fitHeight: true,
    showDraggable: false,
    // Non-dismissible — backdrop tap does nothing; user must submit.
    events: {
      onBackdropTap: () => {},
    },
  };

  // handlers
  handleClose(): void {
    this.store.dispatch(ConsentActions.closeConsentModal());
  }

  handleViewDocument(id: string, documentUrl: string): void {
    this.store.dispatch(ConsentActions.viewConsentDocument({ id, documentUrl }));
  }

  handleToggle(id: string): void {
    this.store.dispatch(ConsentActions.toggleConsentItem({ id }));
  }

  handleLogout(): void {
    this.store.dispatch(GeneralActions.logoutRequested());
  }

  handleSubmit(): void {
    if (!this.isMandatoryConsentComplete$$() || this.isConsentUpdateSubmitting$$()) return;
    const mode = this.consentModalMode$$() ?? 'update';
    this.store.dispatch(ConsentActions.submitConsentUpdate({ consents: this.userConsents$$(), mode, language: this.activeAppLanguage$$() }));
  }
}
